import { useListContext } from "ra-core";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { SortButton } from "@/components/admin/sort-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { useNavigate } from "react-router";

import { TopToolbar } from "../layout/TopToolbar";
import type { SalesIntel } from "../types";
import { ConfidenceBadge } from "./ConfidenceBadge";

export const SalesIntelList = () => {
  return (
    <List
      title={false}
      perPage={25}
      resource="sales_intel"
      sort={{ field: "confidence_score", order: "ASC" }}
      actions={<SalesIntelListActions />}
      pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
      <SalesIntelListContent />
    </List>
  );
};

const SalesIntelListActions = () => (
  <TopToolbar>
    <SortButton
      fields={["confidence_score", "primary_angle", "trigger_count"]}
    />
  </TopToolbar>
);

const SalesIntelListContent = () => {
  const { data: records, isPending, error } = useListContext<SalesIntel>();
  const navigate = useNavigate();

  if (isPending || error) return null;
  if (!records?.length) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No sales intelligence data yet. Data is generated by the emspo-scraper.
      </div>
    );
  }

  return (
    <div className="divide-y">
      <div className="grid grid-cols-6 gap-4 px-4 py-2 text-sm font-medium text-muted-foreground">
        <span className="col-span-2">Company</span>
        <span>Confidence</span>
        <span>Primary Angle</span>
        <span className="text-right">Triggers</span>
        <span>Why Now</span>
      </div>
      {records.map((record) => (
        <div
          key={record.id}
          onClick={() => navigate(`/companies/${record.company_id}/show/intel`)}
          className="grid grid-cols-6 gap-4 px-4 py-3 text-sm hover:bg-muted transition-colors items-center cursor-pointer"
        >
          <div className="col-span-2">
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
              record={record}
            >
              <TextField source="name" className="font-medium" />
            </ReferenceField>
          </div>
          <span>
            <ConfidenceBadge value={record.confidence_score} />
          </span>
          <span>{record.primary_angle || "—"}</span>
          <span className="text-right">{record.trigger_count ?? "—"}</span>
          <span className="truncate">{record.why_now || "—"}</span>
        </div>
      ))}
    </div>
  );
};
